/**
 * PendingMessagesAlert — Dashboard-Widget zeigt fehlgeschlagene Etsy-
 * Nachrichten die manuelle Aktion brauchen (typisch: Etsy Ambiguity —
 * mehrere Message-Icons für 1 Order-ID, Scraper verweigert Auto-Send
 * um Fehl-Empfänger zu vermeiden).
 *
 * Datenquelle: `etsy_message_queue` docs mit status='error' und ohne
 * dismissedAt. Zusätzlich Filter: linked Order muss noch in aktivem
 * Workflow-Stage sein (archivierte/versandte Orders sind nicht mehr
 * relevant).
 *
 * Pro betroffener Order zeigt die Karte:
 *  - Order#, Käufer-Name, Message-Type (Welcome / Reminder / Draft)
 *  - Zeitstempel des letzten Fehlversuchs
 *  - Etsy-Link (öffnet Order-Detail in neuem Tab)
 *  - Text-kopieren-Button (rekonstruiert die geplante Nachricht +
 *    kopiert sie in die Zwischenablage — Alexandra fügt sie dann in
 *    die Etsy-Konversation ein)
 *  - Erledigt-Button (markiert dismissedAt, Alert verschwindet)
 *
 * Deduplizierung: bei mehreren Retry-Fehlversuchen pro (orderId,
 * messageType) wird nur der neueste angezeigt. Erledigt markiert
 * automatisch alle älteren Duplikate.
 */
import React, { useEffect, useState, useMemo } from "react";
import {
  collection, doc, getDoc, onSnapshot, updateDoc, serverTimestamp,
  query, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { SHOP, reviewBase } from "../shop";

const ACTIVE_STAGES = new Set([
  "new", "grafiker", "entwurf_raus", "produktion", "druck", "kerzen",
]);

// Message-type labels for display
const TYPE_LABELS = {
  order_welcome:  { label: "Willkommen (Bestellung)", color: "#805ad5" },
  draft_welcome:  { label: "Willkommen (Entwurf)",    color: "#805ad5" },
  order:          { label: "Reminder Bestellung",     color: "#dd6b20" },
  draft:          { label: "Reminder Entwurf",        color: "#dd6b20" },
};

// Default templates — used if the corresponding config doc field is empty
// (matches scraper's Python defaults in process_etsy_message_queue).
const DEFAULT_ORDER_WELCOME =
  "Hallo {{NAME}},\n\nvielen Dank für deine Bestellung! Bevor wir mit " +
  "der Gestaltung beginnen, bitte einmal kurz bestätigen, dass alle " +
  "Details stimmen.\n\n{{LINK}}\n\nBei Fragen antworte einfach auf " +
  "diese Nachricht.\n\nLiebe Grüße\nDein Team von " + SHOP.name;

const DEFAULT_DRAFT_WELCOME =
  "Hallo {{NAME}},\ndein persönlicher Entwurf ist fertig ✨\n" +
  "👉 Bitte prüfe ihn jetzt hier:\n{{LINK}}\n" +
  "Bitte nimm dir einen Moment Zeit (2-3 min) und prüfe deinen " +
  "Entwurf sorgfältig.\nEr wird genauso produziert, wie du ihn siehst.\n\n" +
  "Bei Fragen: " + SHOP.email + "\n" +
  "Liebe Grüße\nDein Team von " + SHOP.name;

const DEFAULT_ORDER_REMINDER_2 =
  "Hallo {{NAME}},\nwir haben dir bereits zwei E-Mails zur Bestätigung " +
  "deiner Bestellung {{ORDER}} geschickt.\nBitte prüfe auch deinen " +
  "Spam-Ordner.\nLiebe Grüße, dein " + SHOP.name + " Team";

const DEFAULT_ORDER_REMINDER_3 =
  "Hallo {{NAME}},\nletzte Erinnerung: deine Bestellung {{ORDER}} " +
  "wartet noch auf deine Bestätigung.\nLiebe Grüße, dein " + SHOP.name + " Team";

const DEFAULT_DRAFT_REMINDER_2 =
  "Hallo {{NAME}},\nwir wollten nachfragen, ob du unsere E-Mails " +
  "zum Entwurf für Bestellung {{ORDER}} erhalten hast.\n" +
  "Liebe Grüße, dein " + SHOP.name + " Team";

const DEFAULT_DRAFT_REMINDER_3 =
  "Hallo {{NAME}},\nletzte Erinnerung: dein Entwurf für Bestellung " +
  "{{ORDER}} wartet noch auf deine Freigabe.\n" +
  "Liebe Grüße, dein " + SHOP.name + " Team";

// Mirror of scraper's _build_review_url (in main.py):
//   1. draft_id → /review/{draft_id}
//   2. order.reviewToken → /order-review/{reviewToken}
//   3. fallback → /order-review/{order_id}
function buildReviewUrl(order, orderId, draftId) {
  const base = reviewBase();
  if (draftId) return `${base}/review/${draftId}`;
  const token = order?.reviewToken;
  if (token) return `${base}/order-review/${token}`;
  return `${base}/order-review/${orderId}`;
}

// `{{HINWEIS}}` genauso behandeln, wie die E-Mail es tut — und wie
// `main.hinweis_einsetzen` auf der Scraper-Seite. Steht der Platzhalter
// allein auf einer Zeile und es gibt keinen Hinweis, faellt die ganze
// ZEILE weg; sonst bliebe ein Loch im Text.
//
// Hintergrund (25.10.2026): die Vorlage `initialBody` traegt seit dem
// 06.09. eine `{{HINWEIS}}`-Zeile. Die Cloud Function kannte sie, Scraper
// und dieser Kopierknopf nicht — 347 Etsy-Nachrichten gingen mit dem
// rohen Platzhalter an Kunden.
function applyNotice(text, notice) {
  if (!text || !text.includes("{{HINWEIS}}")) return text;
  const n = (notice || "").trim();
  const lines = [];
  for (const z of text.split("\n")) {
    if (z.trim() === "{{HINWEIS}}") {
      if (n) lines.push(n);
      continue;
    }
    lines.push(z.split("{{HINWEIS}}").join(n));
  }
  let out = lines.join("\n");
  while (out.includes("\n\n\n")) out = out.split("\n\n\n").join("\n\n");
  return out;
}

// Render the message for a given queue doc, mirroring the scraper's
// process_etsy_message_queue logic (Python -> JS port, 2026-08-26).
function renderMessage({ queueDoc, order, draft, orderEmailTexts, draftEmailTexts }) {
  const { messageType, orderId, draftId, reminderNumber = 2 } = queueDoc;
  const buyerName = order?.buyerName || "";
  const firstName = (order?.confirmedFirstName)
    || (buyerName.split(" ")[0]) || "Kunde";
  const orderLink = buildReviewUrl(order, orderId, draftId);

  let template = "";
  if (messageType === "order_welcome") {
    template = (orderEmailTexts?.welcomeBody) || DEFAULT_ORDER_WELCOME;
    // Strip {{ITEMS}} — plain-text Etsy message (matches Python)
    template = template.replace("{{ITEMS}}", "").replace("\n\n\n", "\n\n");
  } else if (messageType === "draft_welcome") {
    template = (draftEmailTexts?.initialBody) || DEFAULT_DRAFT_WELCOME;
  } else if (messageType === "order") {
    const key = `orderEtsy${reminderNumber}Reminder`;
    template = orderEmailTexts?.[key]
      || (reminderNumber === 2 ? DEFAULT_ORDER_REMINDER_2 : DEFAULT_ORDER_REMINDER_3);
  } else {  // draft (reminder)
    const key = `etsy${reminderNumber}Reminder`;
    template = draftEmailTexts?.[key]
      || (reminderNumber === 2 ? DEFAULT_DRAFT_REMINDER_2 : DEFAULT_DRAFT_REMINDER_3);
  }

  const text = template
    .replace(/\{\{NAME\}\}/g, firstName)
    .replace(/\{\{ORDER\}\}/g, String(orderId))
    .replace(/\{\{LINK\}\}/g, orderLink);
  return applyNotice(text, draft?.customerNotice);
}

function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Component ────────────────────────────────────────────────────────────

export default function PendingMessagesAlert() {
  const [errorDocs, setErrorDocs] = useState([]);
  const [orders, setOrders] = useState({});          // orderId → orderDoc
  const [drafts, setDrafts] = useState({});          // draftId → draftDoc
  const [orderEmailTexts, setOrderEmailTexts] = useState({});
  const [draftEmailTexts, setDraftEmailTexts] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [dismissing, setDismissing] = useState(new Set());

  // Real-time listener on error docs
  useEffect(() => {
    const q = query(
      collection(db, "etsy_message_queue"),
      where("status", "==", "error")
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = [];
      snap.forEach(d => {
        const x = d.data();
        // Filter out already-dismissed docs client-side (avoids composite index)
        if (!x.dismissedAt) {
          docs.push({ id: d.id, ...x });
        }
      });
      setErrorDocs(docs);
    });
    return unsub;
  }, []);

  // Load templates once (rarely change)
  useEffect(() => {
    getDoc(doc(db, "config", "orderEmailTexts")).then(s => {
      if (s.exists()) setOrderEmailTexts(s.data());
    }).catch(() => {});
    getDoc(doc(db, "config", "emailTexts")).then(s => {
      if (s.exists()) setDraftEmailTexts(s.data());
    }).catch(() => {});
  }, []);

  // Load linked orders for all error docs
  useEffect(() => {
    const missing = errorDocs
      .map(d => d.orderId)
      .filter(oid => oid && !orders[oid]);
    if (missing.length === 0) return;
    const uniqueMissing = [...new Set(missing)];
    Promise.all(
      uniqueMissing.map(oid =>
        getDoc(doc(db, "etsy_orders", oid))
          .then(s => [oid, s.exists() ? s.data() : null])
          .catch(() => [oid, null])
      )
    ).then(results => {
      setOrders(prev => {
        const next = { ...prev };
        results.forEach(([oid, data]) => { next[oid] = data; });
        return next;
      });
    });
  }, [errorDocs, orders]);

  // Entwuerfe nachladen — nur fuer den Hinweis an den Kunden
  // (`customerNotice`). Ohne ihn stuende im kopierten Text ein roher
  // {{HINWEIS}} oder die Information fehlte ganz; siehe applyNotice.
  useEffect(() => {
    const missing = errorDocs
      .map(d => d.draftId)
      .filter(did => did && !(did in drafts));
    if (missing.length === 0) return;
    const uniqueMissing = [...new Set(missing)];
    Promise.all(
      uniqueMissing.map(did =>
        getDoc(doc(db, "drafts", did))
          .then(s => [did, s.exists() ? s.data() : null])
          .catch(() => [did, null])
      )
    ).then(results => {
      setDrafts(prev => {
        const next = { ...prev };
        results.forEach(([did, data]) => { next[did] = data; });
        return next;
      });
    });
  }, [errorDocs, drafts]);

  // ── Filter + dedupe pro (orderId, messageType) ─────────────────
  const actionable = useMemo(() => {
    // Group by (orderId, messageType), keep only newest
    const latestPerKey = new Map();
    for (const doc of errorDocs) {
      const key = `${doc.orderId}:${doc.messageType}`;
      const prev = latestPerKey.get(key);
      const ts = doc.createdAt?.toMillis?.() ?? 0;
      const prevTs = prev?.createdAt?.toMillis?.() ?? 0;
      if (!prev || ts > prevTs) {
        latestPerKey.set(key, doc);
      }
    }

    // Filter: order must exist AND be in an active stage
    const filtered = [];
    for (const doc of latestPerKey.values()) {
      const order = orders[doc.orderId];
      // If order not loaded yet, keep it (loading state)
      if (order === undefined) continue;
      // If order was deleted, skip
      if (order === null) continue;
      // If order archived or unknown stage, skip
      if (!ACTIVE_STAGES.has(order.workflowStage)) continue;
      filtered.push({ ...doc, _order: order });
    }

    // Sort by createdAt desc (newest failure first)
    filtered.sort((a, b) => {
      const ta = a.createdAt?.toMillis?.() ?? 0;
      const tb = b.createdAt?.toMillis?.() ?? 0;
      return tb - ta;
    });
    return filtered;
  }, [errorDocs, orders]);

  async function handleCopyText(item) {
    const text = renderMessage({
      queueDoc: item,
      order: item._order,
      draft: item.draftId ? drafts[item.draftId] : null,
      orderEmailTexts,
      draftEmailTexts,
    });
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(cur => cur === item.id ? null : cur), 2000);
    } catch (e) {
      alert("Kopieren fehlgeschlagen: " + e.message);
    }
  }

  async function handleDismiss(item) {
    setDismissing(prev => new Set(prev).add(item.id));
    try {
      // Also mark all OTHER duplicates (same orderId+messageType) as dismissed
      const dupes = errorDocs.filter(d =>
        d.orderId === item.orderId &&
        d.messageType === item.messageType &&
        !d.dismissedAt
      );
      await Promise.all(dupes.map(d =>
        updateDoc(doc(db, "etsy_message_queue", d.id), {
          dismissedAt: serverTimestamp(),
        })
      ));
    } catch (e) {
      alert("Fehler beim Erledigen: " + e.message);
    } finally {
      setDismissing(prev => {
        const n = new Set(prev);
        n.delete(item.id);
        return n;
      });
    }
  }

  if (actionable.length === 0) return null;

  return (
    <div style={{
      background: "#fff5f5",
      border: "1px solid #feb2b2",
      borderRadius: "12px",
      padding: "1.2em 1.5em",
      marginBottom: "1.5em",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "0.6em",
      }}>
        <div style={{
          fontSize: "0.85rem",
          color: "#c53030",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.03em",
        }}>
          ⚠ Etsy-Nachrichten — manuelle Aktion nötig ({actionable.length})
        </div>
      </div>

      <div style={{ fontSize: "0.8rem", color: "#742a2a", marginBottom: "0.9em", lineHeight: 1.5 }}>
        Diese Bestellungen konnten wegen mehrdeutiger Etsy-Suchergebnisse nicht
        automatisch per Etsy-Nachricht kontaktiert werden. Bitte den Link zur
        Bestellung öffnen, die Nachricht kopieren, in der Etsy-Konversation
        einfügen, senden — dann „Erledigt" klicken.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.6em" }}>
        {actionable.map(item => {
          const tinfo = TYPE_LABELS[item.messageType] || {
            label: item.messageType, color: "#4a5568",
          };
          const order = item._order;
          const isDismissing = dismissing.has(item.id);
          const isCopied = copiedId === item.id;
          const etsyUrl = `https://www.etsy.com/your/orders/sold?search_query=${item.orderId}&order_id=${item.orderId}`;

          return (
            <div key={item.id} style={{
              background: "#fff",
              border: "1px solid #fed7d7",
              borderRadius: "8px",
              padding: "0.7em 1em",
              display: "grid",
              gridTemplateColumns: "auto 1fr auto auto auto",
              gap: "0.8em",
              alignItems: "center",
              fontSize: "0.85rem",
              opacity: isDismissing ? 0.5 : 1,
            }}>
              {/* Order + Buyer */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#2d3748" }}>
                  #{item.orderId}
                </div>
                <div style={{ color: "#718096", fontSize: "0.78rem" }}>
                  {order.buyerName || "—"}
                </div>
              </div>

              {/* Type + Meta */}
              <div style={{ minWidth: 0 }}>
                <div style={{ color: tinfo.color, fontWeight: 600 }}>
                  {tinfo.label}
                  {item.reminderNumber ? ` #${item.reminderNumber}` : ""}
                </div>
                <div style={{ color: "#a0aec0", fontSize: "0.72rem" }}>
                  fehlgeschlagen {fmtDate(item.createdAt)}
                  {" · Stage: "}<strong>{order.workflowStage}</strong>
                </div>
              </div>

              {/* Etsy link */}
              <a
                href={etsyUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#553c9a",
                  color: "#fff",
                  padding: "0.35em 0.75em",
                  borderRadius: "5px",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
                title="Order in Etsy Sellerhub öffnen (neuer Tab)"
              >
                Etsy →
              </a>

              {/* Copy text button */}
              <button
                onClick={() => handleCopyText(item)}
                disabled={isDismissing}
                style={{
                  background: isCopied ? "#48bb78" : "#edf2f7",
                  color: isCopied ? "#fff" : "#2d3748",
                  border: "1px solid " + (isCopied ? "#48bb78" : "#cbd5e0"),
                  padding: "0.35em 0.75em",
                  borderRadius: "5px",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: isDismissing ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
                title="Nachrichten-Text in Zwischenablage kopieren"
              >
                {isCopied ? "✓ kopiert" : "📋 Text"}
              </button>

              {/* Dismiss button */}
              <button
                onClick={() => handleDismiss(item)}
                disabled={isDismissing}
                style={{
                  background: "#fff",
                  color: "#c53030",
                  border: "1px solid #feb2b2",
                  padding: "0.35em 0.75em",
                  borderRadius: "5px",
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  cursor: isDismissing ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
                title="Als erledigt markieren (Alert verschwindet)"
              >
                {isDismissing ? "…" : "✓ Erledigt"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
